import React, { useEffect, useState } from 'react';
import './SignupLoginModal.scss';
import GoogleIcon from './GoogleIcon';
import FacebookIcon from './FacebookIcon';
import { getCookie } from '../utils/utils';
import TagManager from 'react-gtm-module';
import { CloseIcon } from '../common/CloseIcon';
import { Trans, useTranslation } from 'react-i18next';

declare global {
  interface Window {
    google: any;
    FB: any;
    fbAsyncInit: () => void;
  }
}

type Props = {
  email: string;
  setShowLoginView: (showLoginView: boolean) => void;
  setShowSignupView: (showSignupView: boolean) => void;
  toggleSignupLoginModalVisible: () => void;
  validateEmail: (email: string) => string;
  onLoginSuccess: (expires_in: number) => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  setEmail: (email: string) => void;
};

const LoginOrSignup = (props: Props) => {
  const {
    email,
    setEmail,
    setShowLoginView,
    setShowSignupView,
    toggleSignupLoginModalVisible,
    validateEmail,
    onLoginSuccess,
    setIsLoggedIn,
  } = props;
  const { t, i18n } = useTranslation();
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [googleOAuthClient, setGoogleOAuthClient] = useState(null);
  const languageIsEnglish = i18n.language.startsWith('en');

  useEffect(() => {
    initializeOAuthFlow();
    initializeFacebookSDK();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeOAuthFlow = () => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: handleGoogleOAuthResponse,
    });
    setGoogleOAuthClient(client);
  };

  const initializeFacebookSDK = () => {
    const init = () => {
      window.FB.init({
        appId: process.env.REACT_APP_FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
    };
  
    if (window.FB) {
      init();
    } else {
      window.fbAsyncInit = init;
      // Optionally, poll for FB in case fbAsyncInit is missed
      const interval = setInterval(() => {
        if (window.FB) {
          init();
          clearInterval(interval);
        }
      }, 100);
    }
  };

  const handleGoogleOAuthResponse = (response: any) => {
    // "response.code" should be sent to your server so
    // that it can exchange the authorization code for
    // an access token and/or ID token.
    fetch(`${process.env.REACT_APP_API_URL}/verify-google-oauth-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_access_token'),
      },
      body: JSON.stringify({ code: response.code, language: i18n.language }),
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          console.error('Token verification failed:', data.error);
        } else {
          setIsLoggedIn(true);
          onLoginSuccess(data.expires_in);
          TagManager.dataLayer({
            dataLayer: {
              event: 'login_by_google_success',
              category: 'User Interaction',
              action: 'Success',
              label: 'Login by Google Success',
              userId: data.email?.toLowerCase(),
            },
          });
          toggleSignupLoginModalVisible();
        }
      })
      .catch((error) => {
        console.error('Error verifying the OAuth code:', error);
      });
  };

  const handleSubmitCheckIfUserExists = async () => {
    setIsCheckingUser(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/check-if-user-exists?email=${email.toLowerCase()}`,
        {
          method: 'GET',
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          setShowLoginView(true);
        } else {
          setShowSignupView(true);
        }
      } else {
        console.error('Email check failed');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsCheckingUser(false);
    }
  };

  const handleGoogleButtonClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'google_login_or_signup_button_clicked',
        category: 'User Interaction',
        action: 'Click',
        label: 'Google Login or Signup Button',
      },
    });
    googleOAuthClient.requestCode();
  };

  const handleFacebookButtonClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'facebook_login_or_signup_button_clicked',
        category: 'User Interaction',
        action: 'Click',
        label: 'Facebook Login or Signup Button',
      },
    });

    window.FB.login((response: any) => {
      if (response.authResponse) {
        // Send the access token to your backend
        fetch(`${process.env.REACT_APP_API_URL}/verify-facebook-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({ 
            accessToken: response.authResponse.accessToken,
            language: i18n.language 
          }),
          credentials: 'include',
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.error) {
              console.error('Facebook token verification failed:', data.error);
            } else {
              setIsLoggedIn(true);
              onLoginSuccess(data.expires_in);
              TagManager.dataLayer({
                dataLayer: {
                  event: 'login_by_facebook_success',
                  category: 'User Interaction',
                  action: 'Success',
                  label: 'Login by Facebook Success',
                  userId: data.email?.toLowerCase(),
                },
              });
              toggleSignupLoginModalVisible();
            }
          })
          .catch((error) => {
            console.error('Error verifying the Facebook token:', error);
          });
      } else {
        console.log('User cancelled login or did not fully authorize.');
      }
    }, {scope: 'public_profile,email'});
  };

  return (
    <div className="signup-login-modal-overlay">
      <div className="signup-login-modal__info">
        <div className="signup-login-modal__info__title">
          <img
            src="/assets/naama5.png?v=1"
            alt="Jaakko logo"
            width={50}
            height={50}
          />
          <Trans i18nKey="signupLoginModal.infoBox.title" />
        </div>
        <div className="signup-login-modal__info__texts-wrapper">
          <div className="signup-login-modal__info__rectangle-and-text-wrapper">
            <div className="signup-login-modal__info__rectangle-and-text-wrapper__rectangle" />
            <div className="signup-login-modal__info__rectangle-and-text-wrapper__text">
              <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.first" />
            </div>
          </div>
          <div className="signup-login-modal__info__rectangle-and-text-wrapper">
            <div className="signup-login-modal__info__rectangle-and-text-wrapper__rectangle" />
            <div className="signup-login-modal__info__rectangle-and-text-wrapper__text">
              <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.second" />
            </div>
          </div>
          <div className="signup-login-modal__info__rectangle-and-text-wrapper">
            <div className="signup-login-modal__info__rectangle-and-text-wrapper__rectangle" />
            <div className="signup-login-modal__info__rectangle-and-text-wrapper__text">
              <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.third" />
            </div>
          </div>
        </div>
        <div className="signup-login-modal__info__image-wrapper ">
          <img
            src="/assets/cellphones.png"
            alt="Jaakko app on mobile"
            width={262}
          />
        </div>
      </div>
      <div
        className="signup-login-modal signup-login-modal--login"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="signup-login-modal__close-wrapper">
          <button
            onClick={toggleSignupLoginModalVisible}
            className="signup-login-modal__close-wrapper__close-button"
          >
            <CloseIcon size={20} />
          </button>
        </div>
        <div className="signup-login-modal__content">
          <div className="signup-login-modal__content__title">
            <Trans i18nKey="signupLoginModal.loginOrSignup" />
          </div>
        </div>
        <button
          id="custom-google-signin-button"
          className="custom-google-signin-button"
          onClick={handleGoogleButtonClick}
        >
          <div className="custom-google-signin-button__icon">
            <GoogleIcon />
          </div>
          <div className="custom-google-signin-button__text">
            <Trans i18nKey="signupLoginModal.continueWithGoogle" />
          </div>
          <div className="custom-google-signin-button__filler" />
        </button>
        <button
          id="custom-facebook-signin-button"
          className="custom-facebook-signin-button"
          onClick={handleFacebookButtonClick}
        >
          <div className="custom-facebook-signin-button__icon">
            <FacebookIcon />
          </div>
          <div className="custom-facebook-signin-button__text">
            <Trans i18nKey="signupLoginModal.continueWithFacebook" />
          </div>
          <div className="custom-facebook-signin-button__filler" />
        </button>
        <div className="signup-login-modal__content__or">
          <hr />
          <Trans i18nKey="signupLoginModal.orContinueWithEmail" />
          <hr />
        </div>
        <form
          className="signup-login-modal__content__form"
          onSubmit={(e) => {
            e.preventDefault();
            const emailValidationError = validateEmail(email);
            setEmailError(emailValidationError);
            if (!emailValidationError) {
              handleSubmitCheckIfUserExists();
            }
          }}
        >
          <input
            type="email"
            placeholder={t("signupLoginModal.emailPlaceholder")}
            className="signup-login-modal__content__input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          {emailError && (
            <div className="signup-login-modal__content__error">
              {emailError}
            </div>
          )}
          <button
            type="submit"
            className="signup-login-modal__content__button signup-login-modal__content__button--login"
            disabled={isCheckingUser}
          >
            {isCheckingUser ? (
              <span className="loading-dots">
                <span></span>
              </span>
            ) : (
              <Trans i18nKey="signupLoginModal.loginOrSignupButton" />
            )}
          </button>
          <div className="signup-login-modal__content__text">
              <Trans i18nKey="signupLoginModal.registerText.first" />
              <a
                href={languageIsEnglish ? "https://info.jaakko.com/en-gb/terms_and_conditions" : "https://info.jaakko.com/kayttoehdot"}
                target="_blank"
                rel="noreferrer"
                className="signup-login-modal__content__text__link"
              >
                <Trans i18nKey="signupLoginModal.registerText.second" />
              </a>
                <Trans i18nKey="signupLoginModal.registerText.third" />
              <a
                href={languageIsEnglish ? "https://info.jaakko.com/en-gb/privacy_policy" : "https://info.jaakko.com/tietosuoja"}
                target="_blank"
                rel="noreferrer"
                className="signup-login-modal__content__text__link"
              >
                <Trans i18nKey="signupLoginModal.registerText.fourth" />
              </a>
          </div>
        </form>
        <div className="signup-login-modal__info-mobile">
          <div className="signup-login-modal__info-mobile__title">
            <img
              src="/assets/naama5.png?v=1"
              alt="Jaakko logo"
              width={50}
              height={50}
            />
            <Trans i18nKey="signupLoginModal.infoBox.title" />
          </div>
          <div className="signup-login-modal__info-mobile__texts-wrapper">
            <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper">
              <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper__rectangle" />
              <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper__text">
                <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.first" />
              </div>
            </div>
            <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper">
              <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper__rectangle" />
              <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper__text">
                <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.second" />
              </div>
            </div>
            <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper">
              <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper__rectangle" />
              <div className="signup-login-modal__info-mobile__rectangle-and-text-wrapper__text">
                <Trans i18nKey="signupLoginModal.infoBox.bulletPoints.third" />
              </div>
            </div>
          </div>
          <div className="signup-login-modal__info-mobile__image-wrapper ">
            <img
              src="/assets/cellphones.png"
              alt="Jaakko app on mobile"
              width={262}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginOrSignup;
